import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { SelectField } from "@/components/Field";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { useCollection } from "@/lib/hooks/useCollection";
import { CALLABLES } from "@/services/callables";
import { formatDateTime, formatMoney, formatNumber, formatPercent } from "@/lib/format";
import {
  REASON_KEY,
  VERDICT_KEY,
  VERDICT_TONE,
  auditAgeDays,
  auditIsStale,
  verdictOf,
  type BoundaryReport,
} from "./boundary";
import {
  ALERT_KEY,
  ALERT_TONE,
  featureKey,
  fallbackIsNotable,
  isUnhealthy,
  recentMonths,
  share,
  type CostReport,
} from "./cost";
import type { Institution } from "@/types";

/**
 * Assistant cost — and the boundary, first.
 *
 * The spend is useful. The sentence above it is the reason this page exists:
 * the product tells institutions that nothing identifying a child has ever left
 * the system for a model, and this is where that claim stops being an assertion
 * and becomes a number somebody can look at.
 *
 * It is one line, at the top, before anything a reader came here to look up.
 */
export function AiPage() {
  const t = useT();
  const { locale } = useI18n();

  const today = new Date().toISOString();
  const months = useMemo(() => recentMonths(today), [today]);
  const [month, setMonth] = useState(months[0]!);

  const boundary = useCallable<BoundaryReport>(CALLABLES.getAiBoundaryReport);
  const cost = useCallable<CostReport, { month: string }>(CALLABLES.getAiCostReport, { month });
  const { rows: institutions } = useCollection<Institution>("institutions");

  const nameOf = useMemo(() => {
    const names = new Map(institutions.map((institution) => [institution.id, institution.name]));
    return (id: string) => names.get(id) ?? id;
  }, [institutions]);

  const verdict = verdictOf(boundary.data);
  const now = Date.parse(today);
  const report = cost.data;

  return (
    <SectionShell titleKey="ai.title">
      <section aria-labelledby="boundary-heading">
        <h2 id="boundary-heading" className="sr-only">
          {t("ai.boundary.title")}
        </h2>

        {boundary.loading ? (
          <p className="text-sm text-ink-muted">{t("common.loading")}</p>
        ) : (
          <div className={`rounded-md border px-5 py-4 ${VERDICT_TONE[verdict]}`}>
            {/*
              One sentence, and the number in it counts the whole collection
              rather than what happens to be on this page. A panel that quietly
              meant "none in the last twenty" would read as reassurance while
              being wrong, which is the worst thing this page could do.
            */}
            <p className="font-display text-lg">
              {t(VERDICT_KEY[verdict], { count: boundary.data?.total ?? 0 })}
            </p>
            <p className="mt-1 text-sm text-ink-soft">{t("ai.boundary.explain")}</p>

            {boundary.data?.lastAudit ? (
              <p className="mt-2 text-sm text-ink-muted">
                {/*
                  What the audit actually read, not merely when it ran. A check
                  over an empty log says so in its own sentence rather than
                  printing "across 0 calls" beneath a green heading.
                */}
                {t(
                  boundary.data.lastAudit.checked === 0
                    ? "ai.boundary.lastAuditEmpty"
                    : "ai.boundary.lastAudit",
                  {
                    when: formatDateTime(locale, boundary.data.lastAudit.at),
                    checked: boundary.data.lastAudit.checked,
                  }
                )}
              </p>
            ) : null}

            {auditIsStale(boundary.data, now) ? (
              // A different problem from a violation, and it needs saying
              // separately: the daily check has stopped running.
              <p className="mt-2 text-sm text-accent">
                {t("ai.boundary.stale", { days: auditAgeDays(boundary.data, now) ?? 0 })}
              </p>
            ) : null}
          </div>
        )}

        {boundary.data && boundary.data.recent.length > 0 ? (
          <ul className="mt-4 flex flex-col gap-2">
            {boundary.data.recent.map((violation) => (
              <li
                key={violation.id}
                className="rounded border border-accent-soft bg-white px-4 py-3 text-sm"
              >
                <span className="text-ink">{t(REASON_KEY[violation.reason])}</span>
                <span className="ml-3 text-ink-muted">
                  {t("ai.boundary.at", {
                    institution: nameOf(violation.institutionId),
                    feature: t(featureKey(violation.feature)),
                    when: formatDateTime(locale, violation.at),
                  })}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section className="mt-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 className="font-display text-lg text-ink">{t("ai.spend.title")}</h2>
          <SelectField
            labelKey="ai.month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            options={months.map((candidate) => ({ value: candidate, labelKey: candidate }))}
          />
        </div>

        {cost.loading ? (
          <p className="mt-6 text-sm text-ink-muted">{t("common.loading")}</p>
        ) : cost.failed || !report ? (
          <p className="mt-6 text-sm text-accent">{t("error.network.unavailable")}</p>
        ) : (
          <>
            <dl className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { key: "ai.spend.total", value: formatMoney(locale, report.costEur * 100, "EUR") },
                { key: "ai.spend.calls", value: formatNumber(locale, report.calls) },
                { key: "ai.spend.failureRate", value: formatPercent(locale, report.failureRate) },
                { key: "ai.spend.p90", value: t("ai.ms", { ms: report.latency.p90 }) },
              ].map((stat) => (
                <div
                  key={stat.key}
                  className="rounded-md border border-paper-deep bg-white px-5 py-4"
                >
                  <dt className="text-sm text-ink-muted">{t(stat.key)}</dt>
                  <dd className="mt-1 font-display text-2xl text-ink">{stat.value}</dd>
                </div>
              ))}
            </dl>

            {report.alerts.length > 0 ? (
              <div className="mt-8 rounded-md border border-cue-amber bg-cue-amber-soft px-5 py-4">
                <h3 className="font-display text-base text-ink">{t("ai.alert.title")}</h3>
                <ul className="mt-2 flex flex-col gap-1 text-sm">
                  {report.alerts.map((alert) => (
                    <li key={alert.institutionId} className={ALERT_TONE[alert.level]}>
                      {t(ALERT_KEY[alert.level], {
                        institution: nameOf(alert.institutionId),
                        spent: formatMoney(locale, alert.spentEur * 100, "EUR"),
                        budget: formatMoney(locale, report.budgetEur * 100, "EUR"),
                      })}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {report.outliers.length > 0 ? (
              <div className="mt-6 rounded-md border border-paper-deep bg-paper px-5 py-4">
                <h3 className="font-display text-base text-ink">{t("ai.outlier.title")}</h3>
                <p className="mt-1 text-sm text-ink-muted">{t("ai.outlier.explain")}</p>
                <ul className="mt-2 flex flex-col gap-1 text-sm text-ink">
                  {report.outliers.map((outlier) => (
                    <li key={outlier.institutionId}>
                      <Link
                        to={`/institutions/${outlier.institutionId}`}
                        className="underline underline-offset-2"
                      >
                        {nameOf(outlier.institutionId)}
                      </Link>
                      <span className="ml-2 text-ink-muted">
                        {t("ai.outlier.line", {
                          factor: outlier.factor,
                          spent: formatMoney(locale, outlier.costEur * 100, "EUR"),
                          median: formatMoney(locale, outlier.medianEur * 100, "EUR"),
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="mt-10 grid gap-10 lg:grid-cols-2">
              <BucketTable
                titleKey="ai.byFeature"
                rows={report.byFeature.map((bucket) => ({
                  ...bucket,
                  label: t(featureKey(bucket.key)),
                }))}
                total={report.costEur}
              />
              <BucketTable
                titleKey="ai.byModel"
                rows={report.byModel.map((bucket) => ({ ...bucket, label: bucket.key }))}
                total={report.costEur}
              />
            </div>

            <div className="mt-10">
              <BucketTable
                titleKey="ai.byInstitution"
                rows={report.byInstitution.map((bucket) => ({
                  ...bucket,
                  label: nameOf(bucket.key),
                }))}
                total={report.costEur}
              />
            </div>

            <section className="mt-12">
              <h2 className="font-display text-lg text-ink">{t("ai.health.title")}</h2>

              <p className="mt-2 text-sm text-ink-soft">
                {fallbackIsNotable(report.fallback)
                  ? t("ai.health.fallbackHigh", {
                      rate: formatPercent(locale, report.fallback.rate),
                      calls: report.fallback.calls,
                    })
                  : t("ai.health.fallbackNormal", {
                      rate: formatPercent(locale, report.fallback.rate),
                    })}
              </p>

              <div className="mt-4 overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <caption className="sr-only">{t("ai.health.title")}</caption>
                  <thead>
                    <tr className="border-b border-paper-deep text-left text-ink-muted">
                      <th scope="col" className="px-3 py-2.5 font-medium">
                        {t("ai.column.model")}
                      </th>
                      <th scope="col" className="px-3 py-2.5 font-medium">
                        {t("ai.column.calls")}
                      </th>
                      <th scope="col" className="px-3 py-2.5 font-medium">
                        {t("ai.column.errorRate")}
                      </th>
                      <th scope="col" className="px-3 py-2.5 font-medium">
                        {t("ai.column.p50")}
                      </th>
                      <th scope="col" className="px-3 py-2.5 font-medium">
                        {t("ai.column.p90")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.health.map((row) => (
                      <tr key={row.model} className="border-b border-paper-warm last:border-0">
                        <td className="px-3 py-3 text-ink">{row.model}</td>
                        <td className="px-3 py-3 text-ink-soft">
                          {formatNumber(locale, row.calls)}
                        </td>
                        <td
                          className={`px-3 py-3 ${isUnhealthy(row) ? "text-accent" : "text-ink-soft"}`}
                        >
                          {formatPercent(locale, row.errorRate)}
                        </td>
                        <td className="px-3 py-3 text-ink-muted">{t("ai.ms", { ms: row.p50 })}</td>
                        <td className="px-3 py-3 text-ink-muted">{t("ai.ms", { ms: row.p90 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </section>
    </SectionShell>
  );
}

/** One breakdown. The same shape three times, so they read the same way. */
function BucketTable({
  titleKey,
  rows,
  total,
}: {
  titleKey: string;
  rows: Array<{ key: string; label: string; calls: number; costEur: number; failures: number }>;
  total: number;
}) {
  const t = useT();
  const { locale } = useI18n();

  return (
    <div>
      <h3 className="font-display text-base text-ink">{t(titleKey)}</h3>
      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-ink-muted">{t("ai.nothing")}</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{t(titleKey)}</caption>
            <thead>
              <tr className="border-b border-paper-deep text-left text-ink-muted">
                <th scope="col" className="px-3 py-2.5 font-medium">
                  {t("ai.column.name")}
                </th>
                <th scope="col" className="px-3 py-2.5 font-medium">
                  {t("ai.column.calls")}
                </th>
                <th scope="col" className="px-3 py-2.5 font-medium">
                  {t("ai.column.cost")}
                </th>
                <th scope="col" className="px-3 py-2.5 font-medium">
                  {t("ai.column.share")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className="border-b border-paper-warm last:border-0">
                  <td className="px-3 py-3 text-ink">{row.label}</td>
                  <td className="px-3 py-3 text-ink-soft">
                    {row.failures > 0
                      ? t("ai.callsWithFailures", { calls: row.calls, failures: row.failures })
                      : formatNumber(locale, row.calls)}
                  </td>
                  <td className="px-3 py-3 text-ink">
                    {formatMoney(locale, row.costEur * 100, "EUR")}
                  </td>
                  <td className="px-3 py-3 text-ink-muted">
                    {formatPercent(locale, share(row.costEur, total))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
